import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState, useEffect } from "react";
import {
  X,
  Settings,
  Eye,
  Droplets,
  Layers,
  BarChart3,
  Map,
  Crop,
  User,
} from "lucide-react";
import { urls } from "../../services/apiService";

export default function PageManagementDialog({ isOpen, onClose }) {
  const defaultPages = [
    { id: 7, name: "Home", trigger: "home", icon: BarChart3, description: "Main overview and analytics" },
    { id: 2, name: "Field Analysis", trigger: "analytics-dashboard", icon: Map, description: "Crop health and field insights" },
    { id: 4, name: "Yield Prediction", trigger: "yield", icon: Crop, description: "Crop yield forecasting" },
    { id: 1, name: "Profile", trigger: "profile", icon: User, description: "User profile and settings" },
    { id: 6, name: "Soil Analysis", trigger: "soil-analysis", icon: Droplets, description: "Soil health and moisture insights" },
    { id: 5, name: "Crop Classification", trigger: "classification", icon: Layers, description: "Crop classification and detection" },
    { id: 3, name: "Settings", trigger: "settings", icon: Settings, description: "Application settings and preferences" },
  ];

  const [pages, setPages] = useState(defaultPages);

  // Load from backend or localStorage
  useEffect(() => {
    if (!isOpen) return;

    const fetchPageStatus = async () => {
      try {
        const response = await fetch(urls.pages);
        if (!response.ok) throw new Error("Failed to fetch page data");
        const data = await response.json();

        // Merge backend data with UI structure
        const updated = defaultPages.map((page) => {
          const match = data.find((p) => p.page_name === page.trigger);
          return match
            ? { ...page, isActive: match.is_enabled === 1 }
            : { ...page, isActive: true };
        });

        setPages(updated);

        // Save the full backend structure to localStorage
        localStorage.setItem("pageSettings", JSON.stringify(data));
      } catch (error) {
        console.error("Error fetching page statuses:", error);
      }
    };

    fetchPageStatus();
  }, [isOpen]);

  // Toggle and sync with backend + localStorage
  const togglePageStatus = async (pageId) => {
    setPages((prev) => {
      const updated = prev.map((page) =>
        page.id === pageId ? { ...page, isActive: !page.isActive } : page
      );

      // Save immediately
      saveToLocalStorageAndAPI(updated, pageId);

      return updated;
    });
  };

  // Sync function
  const saveToLocalStorageAndAPI = async (updatedPages, pageId) => {
    const localData = JSON.parse(localStorage.getItem("pageSettings")) || [];

    // Find toggled page
    const toggledPage = updatedPages.find((p) => p.id === pageId);
    const { trigger, isActive } = toggledPage;

    // Update backend record
    try {
      await fetch(urls.pages_toggle, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          page_name: trigger,
          is_enabled: isActive ? 1 : 0,
        }),
      });
    } catch (error) {
      console.error("API update failed:", error);
    }

    // Update localStorage structure (same as backend)
    const updatedLocal = localData.map((p) =>
      p.page_name === trigger
        ? { ...p, is_enabled: isActive ? 1 : 0 }
        : p
    );
    localStorage.setItem("pageSettings", JSON.stringify(updatedLocal));
  };

  const activePagesCount = pages.filter((page) => page.isActive).length;

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div className="relative bg-gradient-to-br from-white to-gray-50 p-6 rounded-2xl shadow-2xl w-[90%] max-w-2xl mx-auto border border-gray-200/60">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-xl">
                  <Settings className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Page Management</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {activePagesCount} of {pages.length} pages active
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
              <p className="text-gray-600 text-sm mb-4">
                Toggle between <b>Performing</b> (1) and <b>Maintenance</b> (0).
              </p>

              {pages.map((page) => {
                const Icon = page.icon;
                return (
                  <div
                    key={page.id}
                    className={`flex items-center justify-between p-4 rounded-xl border transition-all duration-200 ${
                      page.isActive
                        ? "bg-green-50 border-green-200"
                        : "bg-red-50 border-red-200"
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div
                        className={`p-2 rounded-lg ${
                          page.isActive
                            ? "bg-green-100 text-green-600"
                            : "bg-red-100 text-red-600"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-800">{page.name}</h3>
                          {page.isActive ? (
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full font-medium">
                              Performing
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full font-medium">
                              Maintenance
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{page.description}</p>
                      </div>
                    </div>

                    {/* Toggle */}
                    <button
                      onClick={() => togglePageStatus(page.id)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                        page.isActive ? "bg-green-500" : "bg-red-400"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          page.isActive ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={onClose}
                className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-xl font-medium transition-all duration-200 border border-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
}
